import type { MedplumClient } from '@medplum/core';
import type { QuestionnaireResponse } from '@medplum/fhirtypes';
import type { Role } from '@/pages/users/types/rbac';

const PER_AUTHOR_PAGE_SIZE = 75;

function sortByLastUpdated(a: QuestionnaireResponse, b: QuestionnaireResponse): number {
  const aTime = a.meta?.lastUpdated ?? a.authored ?? '';
  const bTime = b.meta?.lastUpdated ?? b.authored ?? '';
  return bTime.localeCompare(aTime);
}

function dedupeQuestionnaireResponses(responses: QuestionnaireResponse[]): QuestionnaireResponse[] {
  const byId = new Map<string, QuestionnaireResponse>();
  for (const qr of responses) {
    if (qr.id) byId.set(qr.id, qr);
  }
  return [...byId.values()].sort(sortByLastUpdated);
}

export interface CosignQueueFetchOpts {
  role: Role;
  viewerPractitionerId?: string;
  superviseePractitionerIds: string[];
  fallbackPageSize?: number;
  /** When set (including `[]`), search only these authors and never fall back to an unscoped query. */
  authorIds?: string[];
}

/**
 * Resolve which Practitioner authors to search. `authorIds` override wins.
 * Supervisors default to supervisees (empty list → empty result, no global dump).
 * Providers default to self. Other roles may use an unscoped fallback.
 */
export function resolveCosignQueueAuthorIds(opts: CosignQueueFetchOpts): {
  authorIds: string[];
  useUnscopedFallback: boolean;
} {
  if (opts.authorIds !== undefined) {
    return { authorIds: opts.authorIds, useUnscopedFallback: false };
  }
  if (opts.role === 'supervisor') {
    return { authorIds: opts.superviseePractitionerIds, useUnscopedFallback: false };
  }
  if (opts.role === 'provider' && opts.viewerPractitionerId) {
    return { authorIds: [opts.viewerPractitionerId], useUnscopedFallback: false };
  }
  return { authorIds: [], useUnscopedFallback: true };
}

function extractAuthorPractitionerId(authorRef: string | undefined): string | undefined {
  if (!authorRef) return undefined;
  const direct = authorRef.match(/^Practitioner\/([^/]+)$/);
  if (direct) return direct[1];
  return authorRef.match(/Practitioner\/([^/]+)/)?.[1];
}

/**
 * Load QuestionnaireResponses for the supervision cosign / unsigned-notes queues.
 * Supervisors: one search per supervisee author (avoids missing rows in a global top-N),
 * plus a compartment-limited search so AccessPolicy `author=%profile` does not hide
 * supervisee notes when `author=%supervisee` is not yet on the membership.
 * Providers / own-unsigned: own authored QRs.
 */
export async function fetchCosignQueueQuestionnaireResponses(
  medplum: MedplumClient,
  opts: CosignQueueFetchOpts,
): Promise<QuestionnaireResponse[]> {
  medplum.invalidateSearches('QuestionnaireResponse');

  const { authorIds, useUnscopedFallback } = resolveCosignQueueAuthorIds(opts);

  const batches: QuestionnaireResponse[][] = [];

  if (authorIds.length > 0) {
    const perAuthor = await Promise.all(
      authorIds.map(async (authorId) => {
        try {
          return await medplum.searchResources('QuestionnaireResponse', {
            author: `Practitioner/${authorId}`,
            _sort: '-_lastUpdated',
            _count: String(PER_AUTHOR_PAGE_SIZE),
          });
        } catch {
          return [] as QuestionnaireResponse[];
        }
      }),
    );
    batches.push(...perAuthor);

    if (opts.role === 'supervisor') {
      try {
        const allowed = new Set(authorIds);
        const compartment = await medplum.searchResources('QuestionnaireResponse', {
          _sort: '-_lastUpdated',
          _count: String(Math.max(opts.fallbackPageSize ?? 100, 200)),
        });
        batches.push(
          compartment.filter((qr) => {
            const id = extractAuthorPractitionerId(qr.author?.reference);
            return Boolean(id && allowed.has(id));
          }),
        );
      } catch {
        /* author searches above remain the primary source */
      }
    }
  }

  if (batches.length > 0) {
    return dedupeQuestionnaireResponses(batches.flat());
  }

  if (!useUnscopedFallback) return [];

  const fallbackPageSize = opts.fallbackPageSize ?? 100;
  return medplum.searchResources('QuestionnaireResponse', {
    _sort: '-_lastUpdated',
    _count: String(fallbackPageSize),
  });
}
